import React, { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

/* ─────────────────────────────────────────────────────────────────
   JobContainersModal — list + inline Add/Edit Container form
   Validates container Gross Weight, Net Weight and total Package
   quantity against the job's Cargo Details totals, and shows the
   remaining balance qty as containers are added.

   A single container can now carry MORE THAN ONE package type
   (e.g. 20 Bales + 15 Bags + 10 Cartons) via a repeatable
   qty + unit line editor — same pattern used for jobs.
───────────────────────────────────────────────────────────────── */
const CONTAINER_SIZE_OPTIONS = [
    { value: "", label: "-Select-" },
    { value: "20FT_GENERAL", label: "20FT - GEN" },
    { value: "20FT_HIGH_CUBE", label: "20FT - HC" },
    { value: "20FT_FLAT_TRACK", label: "20FT - FT" },
    { value: "20FT_REEFER", label: "20FT - RE" },
    { value: "20FT_OPEN_TOP", label: "20FT - OT" },
    { value: "20FT_TANK", label: "20FT - TA" },
    { value: "20FT_HARD_TOP", label: "20FT - HT" },
    { value: "40FT_GENERAL", label: "40FT - GEN" },
    { value: "40FT_HIGH_CUBE", label: "40FT - HC" },
    { value: "40FT_FLAT_TRACK", label: "40FT - FT" },
    { value: "40FT_REEFER", label: "40FT - RE" },
    { value: "40FT_OPEN_TOP", label: "40FT - OT" },
    { value: "40FT_TANK", label: "40FT - TA" },
    { value: "40FT_HARD_TOP", label: "40FT - HT" },
    { value: "45FT_STANDARD", label: "45FT - ST" },
];

const PACKAGE_UNIT_OPTIONS = [
    { value: "", label: "-Select-" },
    { value: "BDL", label: "BUNDLE (BDL)" },
    { value: "CAS", label: "CASES (CAS)" },
    { value: "SHT", label: "SHEETS (SHT)" },
    { value: "CHT", label: "CHEST (CHT)" },
    { value: "CLS", label: "COILS (CLS)" },
    { value: "COL", label: "COLLIES (COL)" },
    { value: "CON", label: "CONTAINER (CON)" },
    { value: "CRT", label: "CRATES (CRT)" },
    { value: "CSK", label: "WOODEN CASKS (CSK)" },
    { value: "CTN", label: "CARTONS (CTN)" },
    { value: "CYL", label: "CYLINDER (CYL)" },
    { value: "DRM", label: "DRUMS (DRM)" },
    { value: "ENV", label: "STEEL ENVELOPES (ENV)" },
    { value: "FLK", label: "FLASK (FLK)" },
    { value: "FUT", label: "FUTS (FUT)" },
    { value: "HBK", label: "HABBUCK (HBK)" },
    { value: "JBL", label: "JUMBLE BALE (JBL)" },
    { value: "JTA", label: "JOTTA (JTA)" },
    { value: "KEG", label: "KEGGS (KEG)" },
    { value: "LFT", label: "LIFT (LFT)" },
    { value: "LOG", label: "LOGS (LOG)" },
    { value: "NGT", label: "INGOT (NGT)" },
    { value: "PAL", label: "PALLS (PAL)" },
    { value: "PKG", label: "PACKAGES (PKG)" },
    { value: "PLT", label: "PALLETS (PLT)" },
    { value: "QDS", label: "QUADS (QDS)" },
    { value: "REL", label: "REELS (REL)" },
    { value: "RLS", label: "ROOLS (RLS)" },
    { value: "SKD", label: "SKID & SKIDDED PKGS (SKD)" },
    { value: "SLB", label: "SLABS (SLB)" },
    { value: "TBL", label: "TABLE (TBL)" },
    { value: "TIN", label: "TINS (TIN)" },
    { value: "TRK", label: "TRUNK (TRK)" },
    { value: "UNT", label: "UNITS (UNT)" },
    { value: "PCS", label: "PIECES (PCS)" },
    { value: "BAL", label: "BALES" },
];

const emptyContainerForm = {
    container_id: 0,
    container_no: "",
    seal_no: "",
    container_size: "",
    weight_unit: "mt",
    tare_weight: "",
    gross_weight: "",
    net_weight: "",
    free_days: "",
    shifting_date: "",
    arrival_date: "",
    storage_date: "",
    yard_date: "",
    remarks: "",
};

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

/* ── package line helpers (mirrors the job-level PackageLinesEditor) ── */
const emptyPackageLine = () => ({
    _key: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    no_of_packages: "",
    package_unit: "",
});

/** Human-readable "20 Bales, 15 Bags" summary from a packages array. */
const summarizePackages = (packages) => {
    if (!Array.isArray(packages) || packages.length === 0) return "—";
    return (
        packages
            .filter((p) => p.no_of_packages || p.package_unit)
            .map((p) => `${p.no_of_packages || 0} ${p.package_unit || ""}`.trim())
            .join(", ") || "—"
    );
};

/** Total qty across a packages array (or a legacy single number). */
const totalPackagesQty = (packages) => {
    if (Array.isArray(packages)) {
        return packages.reduce((sum, p) => sum + (parseFloat(p.no_of_packages) || 0), 0);
    }
    return num(packages);
};

/* ─────────────────────────────────────────────────────────────────
   ContainerPackageLinesEditor — repeatable "qty + unit" rows so a
   single container can carry more than one package type.
───────────────────────────────────────────────────────────────── */
const ContainerPackageLinesEditor = ({ lines, onChange, error }) => {
    const updateLine = (key, field, value) => {
        onChange(lines.map((l) => (l._key === key ? { ...l, [field]: value } : l)));
    };

    const addLine = () => onChange([...lines, emptyPackageLine()]);

    const removeLine = (key) => {
        if (lines.length === 1) {
            onChange([emptyPackageLine()]);
            return;
        }
        onChange(lines.filter((l) => l._key !== key));
    };

    const total = lines.reduce((sum, l) => sum + (parseFloat(l.no_of_packages) || 0), 0);

    return (
        <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label fw-semibold mb-0" style={{ fontSize: 12 }}>
                    No. of Packages / Package Unit <span className="text-danger">*</span>
                    <span className="text-muted fw-normal"> (add a line per package type)</span>
                </label>
                <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>
                    Total: {total || 0}
                </span>
            </div>

            <div className="d-flex flex-column gap-2">
                {lines.map((line, idx) => (
                    <div className="row g-2 align-items-center" key={line._key}>
                        <div className="col-4 col-md-4">
                            <input
                                type="number"
                                min="0"
                                className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
                                placeholder="No. of Packages"
                                value={line.no_of_packages}
                                onChange={(e) => updateLine(line._key, "no_of_packages", e.target.value)}
                            />
                        </div>
                        <div className="col-5 col-md-5">
                            <select
                                className={`form-select form-select-sm ${error ? "is-invalid" : ""}`}
                                value={line.package_unit}
                                onChange={(e) => updateLine(line._key, "package_unit", e.target.value)}
                            >
                                {PACKAGE_UNIT_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-auto">
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                title="Remove line"
                                onClick={() => removeLine(line._key)}
                            >
                                <i className="ri-close-line" />
                            </button>
                        </div>
                        {idx === lines.length - 1 && (
                            <div className="col-auto">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={addLine}
                                    title="Add another package type"
                                >
                                    <i className="ri-add-line me-1" />
                                    Add
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {error && <div className="text-danger mt-1" style={{ fontSize: 11 }}>{error}</div>}
        </div>
    );
};

const JobContainersModal = ({ show, job, onClose }) => {
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyContainerForm);
    const [packageLines, setPackageLines] = useState([emptyPackageLine()]);
    const [errors, setErrors] = useState({});
    const [editingId, setEditingId] = useState(null); // null = adding new

    /* ── fetch all containers, filter client-side by this job ── */
    const fetchContainers = useCallback(async () => {
        if (!job) return;
        setLoading(true);
        try {
            const res = await post("/all_jobs/containers/list");
            if (res.status === 200) {
                const filtered = (res.data || []).filter(
                    (c) => String(c.job_id_fk) === String(job.job_id)
                );
                setContainers(filtered);
            } else {
                setContainers([]);
            }
        } catch {
            setContainers([]);
        } finally {
            setLoading(false);
        }
    }, [job]);

    useEffect(() => {
        if (show && job) {
            fetchContainers();
            setShowForm(false);
            setForm(emptyContainerForm);
            setPackageLines([emptyPackageLine()]);
            setErrors({});
            setEditingId(null);
        }
    }, [show, job, fetchContainers]);

    /* ── job totals (Cargo Details) ── */
    const jobGross = num(job?.gross_weight);
    const jobNet = num(job?.net_weight);
    const jobPackages = totalPackagesQty(job?.packages);
    const allowedContainers = parseInt(job?.no_of_containers, 10) || 0;

    /* ── sums of containers already added, excluding the one being edited ── */
    const totals = useMemo(() => {
        return containers.reduce(
            (acc, c) => {
                if (editingId && String(c.container_id) === String(editingId)) return acc;
                acc.gross += num(c.gross_weight);
                acc.net += num(c.net_weight);
                acc.packages += totalPackagesQty(c.packages);
                return acc;
            },
            { gross: 0, net: 0, packages: 0 }
        );
    }, [containers, editingId]);

    /* ── balances (job total minus what's already used) ── */
    const balanceGross = +(jobGross - totals.gross).toFixed(3);
    const balanceNet = +(jobNet - totals.net).toFixed(3);
    const balancePackages = jobPackages - totals.packages;

    /* ── package lines helpers ── */
    const getValidPackageLines = useCallback(() => {
        return packageLines.filter((l) => l.no_of_packages !== "" || l.package_unit !== "");
    }, [packageLines]);

    if (!show) return null;

    const addedCount = containers.length;
    const isCountMismatch = allowedContainers > 0 && addedCount !== allowedContainers;
    const atCountLimit = allowedContainers > 0 && addedCount >= allowedContainers && !editingId;

    /* ── form change ── */
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    /* ── validation ── */
    const validate = () => {
        const errs = {};

        if (!form.container_no?.trim()) {
            errs.container_no = "Container No. is required";
        } else if (
            containers.some(
                (c) =>
                    (!editingId || String(c.container_id) !== String(editingId)) &&
                    String(c.container_no).trim().toLowerCase() ===
                    form.container_no.trim().toLowerCase()
            )
        ) {
            errs.container_no = "This container number is already added for this job";
        }

        const tare = num(form.tare_weight);
        const gross = num(form.gross_weight);
        const net = num(form.net_weight);

        if (form.tare_weight && tare < 0) errs.tare_weight = "Cannot be negative";

        // ── Gross Weight (required) ──
        if (form.gross_weight === "" || form.gross_weight === null || form.gross_weight === undefined) {
            errs.gross_weight = "Gross Weight is required";
        } else if (gross <= 0) {
            errs.gross_weight = "Gross Weight must be greater than 0";
        } else if (jobGross > 0 && gross > balanceGross) {
            errs.gross_weight = `Exceeds available Gross Weight balance (${balanceGross} ${job?.weight_unit || ""})`;
        } else if (form.tare_weight && gross < tare) {
            errs.gross_weight = "Gross weight cannot be less than tare weight";
        }

        // ── Net Weight (required) ──
        if (form.net_weight === "" || form.net_weight === null || form.net_weight === undefined) {
            errs.net_weight = "Net Weight is required";
        } else if (net <= 0) {
            errs.net_weight = "Net Weight must be greater than 0";
        } else if (jobNet > 0 && net > balanceNet) {
            errs.net_weight = `Exceeds available Net Weight balance (${balanceNet} ${job?.weight_unit || ""})`;
        } else if (!errs.gross_weight && form.gross_weight && net > gross) {
            errs.net_weight = "Net weight cannot be greater than gross weight";
        }

        // ── Package lines validation (multiple package types allowed, at least one required) ──
        const validLines = getValidPackageLines();
        if (validLines.length === 0) {
            errs.packages = "Add at least one package line (No. of Packages + Package Unit)";
        } else {
            const incompleteLine = validLines.find((l) => !l.no_of_packages || !l.package_unit);
            if (incompleteLine) {
                errs.packages = "Every package line needs both a quantity and a unit";
            } else {
                const totalPkgs = validLines.reduce((sum, l) => sum + (parseFloat(l.no_of_packages) || 0), 0);
                if (totalPkgs <= 0) {
                    errs.packages = "No. of Packages must be greater than 0";
                } else if (jobPackages > 0 && totalPkgs > balancePackages) {
                    errs.packages = `Exceeds available package balance (${balancePackages})`;
                }
            }
        }

        if (form.free_days && num(form.free_days) < 0) {
            errs.free_days = "Cannot be negative";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    /* ── open form for a new container ── */
    const handleOpenAddForm = () => {
        if (atCountLimit) {
            Swal.fire({
                icon: "warning",
                title: "Container Limit Reached",
                text: `Cargo Details declares ${allowedContainers} container(s) — you've already added ${addedCount}. Update "No. of Containers" in the job first if you need to add more.`,
            });
            return;
        }
        setEditingId(null);
        setForm(emptyContainerForm);
        setPackageLines([emptyPackageLine()]);
        setErrors({});
        setShowForm(true);
    };

    /* ── open form pre-filled for editing ── */
    const handleOpenEditForm = (c) => {
        setEditingId(c.container_id);
        setForm({
            container_id: c.container_id,
            container_no: c.container_no || "",
            seal_no: c.seal_no || "",
            container_size: c.container_size || "",
            weight_unit: c.weight_unit || "mt",
            tare_weight: c.tare_weight ?? "",
            gross_weight: c.gross_weight ?? "",
            net_weight: c.net_weight ?? "",
            free_days: c.free_days ?? "",
            shifting_date: c.shifting_date ?? "",
            arrival_date: c.arrival_date || "",
            storage_date: c.storage_date || "",
            yard_date: c.yard_date || "",
            remarks: c.remarks || "",
        });

        // Prefer the fetched `packages` breakdown; fall back to the
        // legacy single no_of_packages/package_unit if that's all we have.
        if (Array.isArray(c.packages) && c.packages.length > 0) {
            setPackageLines(
                c.packages.map((p) => ({
                    _key: `pl_${p.package_id ?? Math.random()}`,
                    no_of_packages: p.no_of_packages ?? "",
                    package_unit: p.package_unit ?? "",
                }))
            );
        } else if (c.no_of_packages || c.package_unit) {
            setPackageLines([
                { ...emptyPackageLine(), no_of_packages: c.no_of_packages ?? "", package_unit: c.package_unit || "" },
            ]);
        } else {
            setPackageLines([emptyPackageLine()]);
        }

        setErrors({});
        setShowForm(true);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setErrors({});
        setEditingId(null);
        setForm(emptyContainerForm);
        setPackageLines([emptyPackageLine()]);
    };

    /* ── save (create or update) ── */
    const handleSave = async () => {
        if (!validate()) return;

        setSaving(true);
        try {
            const payload = {
                ...form,
                container_id: editingId || 0,
                job_id_fk: job.job_id,
                container_type: job.container_type || "",
                cargo_description: job.cargo_desc || "",
                packages: getValidPackageLines().map(({ no_of_packages, package_unit }) => ({
                    no_of_packages,
                    package_unit,
                })),
            };
            const res = await post("/all_jobs/containers", payload);
            if (res.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: editingId ? "Container Updated" : "Container Added",
                    timer: 1500,
                    showConfirmButton: false,
                });
                handleCancelForm();
                fetchContainers();
            } else {
                Swal.fire({ icon: "error", title: "Save Failed", text: res.message });
            }
        } catch (err) {
            Swal.fire({ icon: "error", title: "Error", text: err.message });
        } finally {
            setSaving(false);
        }
    };

    /* ── delete ── */
    const handleDelete = async (container_id) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Delete container?",
            text: "This cannot be undone.",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await post("/all_jobs/containers/delete", { container_id });
            if (res.status === 200) {
                Swal.fire({ icon: "success", title: "Deleted", timer: 1200, showConfirmButton: false });
                if (editingId === container_id) handleCancelForm();
                fetchContainers();
            }
        } catch (err) {
            Swal.fire({ icon: "error", title: "Error", text: err.message });
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} />

            {/* Modal */}
            <div className="modal fade show d-block" style={{ zIndex: 1056 }} tabIndex="-1">
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content" style={{ borderRadius: 12, overflow: "hidden" }}>

                        {/* Header */}
                        <div className="modal-header" style={{ background: "linear-gradient(135deg,#1a3c6e,#2563eb)", color: "#fff" }}>
                            <div>
                                <h5 className="modal-title mb-0">
                                    <i className="ri-container-line me-2" />
                                    Containers
                                </h5>
                                {job && (
                                    <small style={{ opacity: 0.85 }}>
                                        BL: <strong>{job.bl_number}</strong>
                                        &nbsp;·&nbsp;
                                        {job.consignees_name || ""}
                                    </small>
                                )}
                            </div>
                            <button type="button" className="btn-close btn-close-white" onClick={onClose} />
                        </div>

                        {/* Body */}
                        <div className="modal-body" style={{ background: "#f1f5f9", minHeight: 280 }}>

                            {/* Container count summary */}
                            <div
                                className="d-flex align-items-center justify-content-between mb-2 p-2 rounded flex-wrap gap-2"
                                style={{
                                    background: isCountMismatch ? "#fef3c7" : "#dcfce7",
                                    border: `1px solid ${isCountMismatch ? "#fbbf24" : "#86efac"}`,
                                }}
                            >
                                <span style={{ fontSize: 13, fontWeight: 600, color: isCountMismatch ? "#92400e" : "#166534" }}>
                                    <i className={`ri-${isCountMismatch ? "error-warning" : "checkbox-circle"}-line me-1`} />
                                    {addedCount} of {allowedContainers || "—"} container(s) added
                                </span>
                                {!showForm && (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={handleOpenAddForm}
                                        disabled={atCountLimit}
                                        title={atCountLimit ? "Container limit reached" : "Add a container"}
                                    >
                                        <i className="ri-add-line me-1" />
                                        Add Container
                                    </button>
                                )}
                            </div>

                            {/* Qty balance summary (Gross Wt / Net Wt / Packages) */}
                            {(jobGross > 0 || jobNet > 0 || jobPackages > 0) && (
                                <div
                                    className="d-flex flex-wrap gap-2 mb-3 p-2 rounded"
                                    style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
                                >
                                    {jobGross > 0 && (
                                        <span
                                            className={`badge ${balanceGross < 0 ? "bg-danger" : "bg-primary"}`}
                                            style={{ fontSize: 11 }}
                                        >
                                            Gross Wt Balance: {balanceGross} / {jobGross} {job?.weight_unit || ""}
                                        </span>
                                    )}
                                    {jobNet > 0 && (
                                        <span
                                            className={`badge ${balanceNet < 0 ? "bg-danger" : "bg-primary"}`}
                                            style={{ fontSize: 11 }}
                                        >
                                            Net Wt Balance: {balanceNet} / {jobNet} {job?.weight_unit || ""}
                                        </span>
                                    )}
                                    {jobPackages > 0 && (
                                        <span
                                            className={`badge ${balancePackages < 0 ? "bg-danger" : "bg-primary"}`}
                                            style={{ fontSize: 11 }}
                                        >
                                            Packages Balance: {balancePackages} / {jobPackages}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* ── Inline add/edit container form ── */}
                            {showForm && (
                                <div className="card mb-3 shadow-sm border-0">
                                    <div className="card-header py-2" style={{ background: "#fff", borderBottom: "2px solid #e2e8f0" }}>
                                        <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                                            <i className={`ri-${editingId ? "edit-2" : "add-box"}-line me-1 text-primary`} />
                                            {editingId ? "Edit Container" : "New Container"}
                                        </span>
                                    </div>
                                    <div className="card-body" style={{ background: "#fff" }}>
                                        <div className="row g-3">

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                                                    Container No. <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="container_no"
                                                    className={`form-control form-control-sm ${errors.container_no ? "is-invalid" : ""}`}
                                                    value={form.container_no}
                                                    onChange={handleFormChange}
                                                    placeholder="e.g. TCLU1234567"
                                                />
                                                {errors.container_no && <div className="invalid-feedback">{errors.container_no}</div>}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Seal No.</label>
                                                <input
                                                    type="text"
                                                    name="seal_no"
                                                    className="form-control form-control-sm"
                                                    value={form.seal_no}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Container Size</label>
                                                <select
                                                    name="container_size"
                                                    className="form-select form-select-sm"
                                                    value={form.container_size}
                                                    onChange={handleFormChange}
                                                >
                                                    {CONTAINER_SIZE_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Weight Unit</label>
                                                <select
                                                    name="weight_unit"
                                                    className="form-select form-select-sm"
                                                    value={form.weight_unit}
                                                    onChange={handleFormChange}
                                                >
                                                    <option value="mt">MT</option>
                                                    <option value="kg">KG</option>
                                                </select>
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Tare Weight</label>
                                                <input
                                                    type="number"
                                                    name="tare_weight"
                                                    className={`form-control form-control-sm ${errors.tare_weight ? "is-invalid" : ""}`}
                                                    value={form.tare_weight}
                                                    onChange={handleFormChange}
                                                    min="0"
                                                />
                                                {errors.tare_weight && <div className="invalid-feedback">{errors.tare_weight}</div>}
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                                                    Gross Weight <span className="text-danger">*</span>
                                                    {jobGross > 0 && (
                                                        <span className="text-muted fw-normal"> (bal: {balanceGross})</span>
                                                    )}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="gross_weight"
                                                    className={`form-control form-control-sm ${errors.gross_weight ? "is-invalid" : ""}`}
                                                    value={form.gross_weight}
                                                    onChange={handleFormChange}
                                                    min="0"
                                                />
                                                {errors.gross_weight && <div className="invalid-feedback">{errors.gross_weight}</div>}
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>
                                                    Net Weight <span className="text-danger">*</span>
                                                    {jobNet > 0 && (
                                                        <span className="text-muted fw-normal"> (bal: {balanceNet})</span>
                                                    )}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="net_weight"
                                                    className={`form-control form-control-sm ${errors.net_weight ? "is-invalid" : ""}`}
                                                    value={form.net_weight}
                                                    onChange={handleFormChange}
                                                    min="0"
                                                />
                                                {errors.net_weight && <div className="invalid-feedback">{errors.net_weight}</div>}
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Free Days</label>
                                                <input
                                                    type="number"
                                                    name="free_days"
                                                    className={`form-control form-control-sm ${errors.free_days ? "is-invalid" : ""}`}
                                                    value={form.free_days}
                                                    onChange={handleFormChange}
                                                    min="0"
                                                />
                                                {errors.free_days && <div className="invalid-feedback">{errors.free_days}</div>}
                                            </div>

                                            {/* ── Multiple Package Types (qty + unit, repeatable) ── */}
                                            <ContainerPackageLinesEditor
                                                lines={packageLines}
                                                onChange={setPackageLines}
                                                error={errors.packages}
                                            />

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Shifting Date</label>
                                                <input
                                                    type="date"
                                                    name="shifting_date"
                                                    className="form-control form-control-sm"
                                                    value={form.shifting_date}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Arrival Date</label>
                                                <input
                                                    type="date"
                                                    name="arrival_date"
                                                    className="form-control form-control-sm"
                                                    value={form.arrival_date}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>CFS Storage Date</label>
                                                <input
                                                    type="date"
                                                    name="storage_date"
                                                    className="form-control form-control-sm"
                                                    value={form.storage_date}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Empty Return Date</label>
                                                <input
                                                    type="date"
                                                    name="yard_date"
                                                    className="form-control form-control-sm"
                                                    value={form.yard_date}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-md-12">
                                                <label className="form-label fw-semibold" style={{ fontSize: 12 }}>Remarks</label>
                                                <textarea
                                                    name="remarks"
                                                    className="form-control form-control-sm"
                                                    rows={2}
                                                    value={form.remarks}
                                                    onChange={handleFormChange}
                                                />
                                            </div>

                                            <div className="col-12 d-flex justify-content-end gap-2 mt-2">
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={handleCancelForm}
                                                    disabled={saving}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={handleSave}
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-1" />
                                                            Saving…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className={`ri-${editingId ? "save" : "save"}-line me-1`} />
                                                            {editingId ? "Update Container" : "Save Container"}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Existing containers list ── */}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" />
                                    <div className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading containers…</div>
                                </div>
                            ) : containers.length === 0 ? (
                                !showForm && (
                                    <div className="text-center py-5 text-muted">
                                        <i className="ri-container-line" style={{ fontSize: 52, opacity: 0.25 }} />
                                        <div className="mt-2" style={{ fontSize: 13 }}>No containers added for this job yet.</div>
                                    </div>
                                )
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {containers.map((c) => (
                                        <div
                                            key={c.container_id}
                                            className="d-flex align-items-center gap-3 p-2 rounded"
                                            style={{
                                                background: editingId === c.container_id ? "#eff6ff" : "#fff",
                                                border: `1px solid ${editingId === c.container_id ? "#93c5fd" : "#e2e8f0"}`,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                                                    background: "#2563eb15",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}
                                            >
                                                <i className="ri-archive-2-line" style={{ fontSize: 20, color: "#2563eb" }} />
                                            </div>

                                            <div className="flex-fill" style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ fontSize: 13, color: "#0f172a" }}>
                                                    {c.container_no || "—"}
                                                </div>
                                                <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>Seal: {c.seal_no || "—"}</span>
                                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>Size: {c.container_size || "—"}</span>
                                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                                                        Gross Wt: {c.gross_weight || "—"} {c.weight_unit || ""}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                                                        Net Wt: {c.net_weight || "—"} {c.weight_unit || ""}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: "#94a3b8" }} title={summarizePackages(c.packages)}>
                                                        Pkgs: {summarizePackages(c.packages)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="d-flex gap-2 flex-shrink-0">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    style={{ fontSize: 11, padding: "3px 10px" }}
                                                    title="Edit"
                                                    onClick={() => handleOpenEditForm(c)}
                                                >
                                                    <i className="ri-edit-2-line" />
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    style={{ fontSize: 11, padding: "3px 10px" }}
                                                    title="Delete"
                                                    onClick={() => handleDelete(c.container_id)}
                                                >
                                                    <i className="ri-delete-bin-line" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="modal-footer" style={{ background: "#f8fafc" }}>
                            <small className="text-muted me-auto" style={{ fontSize: 11 }}>
                                <i className="ri-information-line me-1" />
                                Container totals are validated against Cargo Details.
                            </small>
                            <button className="btn btn-secondary btn-sm" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default JobContainersModal;